import BrandMark from "../components/brand/BrandMark";
import AuthPlanCard from "../components/auth/AuthPlanCard";
import { useState } from "react";
import { usePricing } from "../context/PricingContext";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  Eye, EyeOff, ArrowLeft, Phone, Shield, 
  Smartphone, Bell, Lock, CheckCircle2, Globe 
} from "lucide-react";
import { COUNTRIES } from "../utils/countries";
import { useI18n } from "../i18n";

const Register = () => {
  const { setCountry: setPricingCountry } = usePricing();
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    password: "",
    confirmPassword: "",
    country_code: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleCountryChange = (e) => {
    const iso = e.target.value;
    setFormData({ ...formData, country_code: iso });
    // RC-WEB-2 §4 — el país elegido dispara la consulta al API canónico de planes
    if (iso) setPricingCountry(iso);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (loading) return;
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Las contraseñas no coinciden");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error("Por favor ingresa un correo válido");
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...userData } = formData;
      // CORTE 5-B — atribución: el ref guardado en la visita se graba en la cuenta
      try {
        const ref = localStorage.getItem("avisocar_ref");
        if (ref) userData.referred_by_code = ref;
      } catch (_) { /* noop */ }
      await register(userData);
      toast.success("¡Cuenta creada exitosamente!");
      navigate("/dashboard");
    } catch (error) {
      console.error("Registration error:", error);
      const message = error.response?.data?.detail || error.message || "Error al crear cuenta";
      
      // Check for duplicate email error
      if (message.toLowerCase().includes("email") || message.toLowerCase().includes("correo") || message.toLowerCase().includes("existe") || message.toLowerCase().includes("registrado")) {
        toast.error("⚠️ Este correo ya está registrado. Inicia sesión.");
      } else {
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Phone, text: t("auth.benefit_calls") },
    { icon: Smartphone, text: t("auth.benefit_no_apps") },
    { icon: Bell, text: t("auth.benefit_alerts") },
    { icon: Shield, text: t("auth.benefit_protection") }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex overflow-hidden">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 py-12 lg:px-16">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-500 hover:text-[#0B2545] transition-colors mb-8 w-fit"
          data-testid="register-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t("auth.back")}</span>
        </Link>

        {/* Logo */}
        <div className="flex items-center gap-3 mb-8">
          <BrandMark size="md" onDark asLink={false} />
        </div>

        {/* Form Card with Glassmorphism */}
        <div className="bg-slate-100 backdrop-blur-xl rounded-2xl p-8 border border-slate-300 shadow-2xl animate-fade-in">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-[#0B2545] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("auth.register_title")}
            </h1>
            <p className="text-slate-500">
              {t("auth.register_subtitle")}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="full_name" className="text-slate-600 text-sm font-medium">
                {t("auth.full_name")}
              </Label>
              <Input
                id="full_name"
                name="full_name"
                type="text"
                placeholder="Juan Pérez"
                value={formData.full_name}
                onChange={handleChange}
                required
                className="h-12 bg-white border-slate-200 text-[#0B2545] placeholder:text-slate-400 focus:border-[#1B84FF] focus:ring-[#1B84FF]/20 rounded-xl"
                autoComplete="name"
                data-testid="register-name-input"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-600 text-sm font-medium">
                {t("auth.email")}
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="tu@email.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="h-12 bg-white border-slate-200 text-[#0B2545] placeholder:text-slate-400 focus:border-[#1B84FF] focus:ring-[#1B84FF]/20 rounded-xl"
                autoComplete="email"
                inputMode="email"
                data-testid="register-email-input"
              />
            </div>

            {/* País */}
            <div className="space-y-2">
              <Label htmlFor="country_code" className="text-slate-600 text-sm font-medium">
                {t("auth.country")}
              </Label>
              <div className="relative">
                <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 pointer-events-none" />
                <select
                  id="country_code"
                  name="country_code"
                  value={formData.country_code}
                  onChange={handleCountryChange}
                  required
                  className="w-full h-12 pl-10 pr-3 rounded-xl bg-white border border-slate-200 text-[#0B2545] text-sm focus:border-[#1B84FF] outline-none appearance-none"
                  data-testid="register-country-select"
                >
                  <option value="" disabled className="bg-[#0B2545] text-slate-500">
                    — {t("auth.country")} —
                  </option>
                  {COUNTRIES.map((c) => (
                    <option key={c.iso} value={c.iso} className="bg-[#0B2545] text-white">
                      {c.flag} {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* iter66 — sin teléfono: anonimato desde el registro */}
            <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="register-no-phone-note">
              <Lock className="w-3 h-3 text-[#1B84FF] flex-shrink-0" />
              {t("auth.no_phone_note")}
            </p>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-600 text-sm font-medium">
                {t("auth.password")}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="h-12 bg-white border-slate-200 text-[#0B2545] placeholder:text-slate-400 focus:border-[#1B84FF] focus:ring-[#1B84FF]/20 rounded-xl pr-12"
                  autoComplete="new-password"
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#0B2545] transition-colors"
                  aria-label={showPassword ? t("auth.hide_password") : t("auth.show_password")}
                  aria-pressed={showPassword}
                  data-testid="register-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-600 text-sm font-medium">
                {t("auth.confirm_password")}
              </Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                  className="h-12 pr-11 bg-white border-slate-200 text-[#0B2545] placeholder:text-slate-400 focus:border-[#1B84FF] focus:ring-[#1B84FF]/20 rounded-xl"
                  data-testid="register-confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((v) => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-[#1B84FF]"
                  aria-label={showConfirmPassword ? t("auth.hide_password") : t("auth.show_password")}
                  aria-pressed={showConfirmPassword}
                  data-testid="register-toggle-confirm-password"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-12 rounded-xl bg-[#1B84FF] hover:bg-[#0F6FDB] text-white font-semibold shadow-lg shadow-[#1B84FF]/30 transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t("auth.creating")}
                </span>
              ) : t("auth.register_btn")}
            </Button>
          </form>

          {/* Login Link */}
          <div className="mt-6 text-center">
            <p className="text-slate-500">
              {t("auth.have_account")}{" "}
              <Link 
                to="/login" 
                className="text-[#1B84FF] hover:text-[#0F6FDB] font-semibold transition-colors"
                data-testid="register-login-link"
              >
                {t("auth.login_here")}
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Benefits & Image (Desktop only) */}
      <div className="hidden lg:flex w-1/2 flex-col justify-center items-center p-12 relative">
        {/* Decorative gradients */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-[#1B84FF]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-blue-500/20 rounded-full blur-3xl"></div>

        {/* Benefits Card */}
        <div className="bg-white backdrop-blur-lg rounded-2xl p-8 border border-slate-200 max-w-md z-10">
          <h2 className="text-2xl font-bold text-[#0B2545] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t("auth.why_avisocar")}
          </h2>
          
          <div className="space-y-4">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="flex items-center gap-4 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-10 h-10 bg-[#1B84FF]/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-5 h-5 text-[#1B84FF]" />
                </div>
                <span className="text-[#0B2545] font-medium">{benefit.text}</span>
                <CheckCircle2 className="w-5 h-5 text-[#1B84FF] ml-auto" />
              </div>
            ))}
          </div>

          {/* RC-WEB-2 — plan publicado + país elegido en el formulario */}
          <AuthPlanCard testId="register-price" />
        </div>

        {/* Sticker Preview Image */}
        <div className="mt-8 relative z-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_canva-stickers/artifacts/27dohpu7_image.png"
            alt="AvisoCar Sticker Preview"
            className="w-80 rounded-2xl shadow-2xl opacity-80 hover:opacity-100 transition-opacity"
          />
        </div>
      </div>

      {/* Custom animation styles */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-shake {
          animation: shake 0.3s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default Register;
